import React, { useEffect, useState, useCallback } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Button, Grid, TextField, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getTagsReport } from '../../services/tags.api';
import { format, subDays } from 'date-fns';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement
);

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        height: 450,
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5),
            height: 400,
        },
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
        fontSize: '18px',
        color: '#1F2937',
    },
    filtersContainer: {
        marginBottom: theme.spacing(2.5),
    },
    chartContainer: {
        flex: 1,
        minHeight: 300,
        position: 'relative',
    },
    totalContainer: {
        textAlign: 'center',
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
        fontWeight: 'bold',
    }
}));

export const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        datalabels: {
            display: true,
            anchor: 'end',
            align: "top",
            color: "#374151",
            font: {
                size: 12,
                weight: "600"
            },
            formatter: (value) => value || '',
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
                size: 14,
                weight: 'bold'
            },
            bodyFont: {
                size: 13
            },
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
                label: function(context) {
                    return `${context.label}: ${context.parsed.y} ${i18n.t("tagsReport.chart.tooltip")}`;
                }
            }
        }
    },
    scales: {
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false,
            },
            ticks: {
                font: {
                    size: 11
                },
                color: '#6B7280',
            },
            title: {
                display: true,
                text: i18n.t("tagsReport.chart.yAxisTitle"),
                font: {
                    size: 12,
                    weight: '600'
                },
                color: '#6B7280',
            }
        },
        x: {
            grid: {
                display: false,
            },
            ticks: {
                font: {
                    size: 11
                },
                color: '#6B7280',
                maxRotation: 45,
                minRotation: 45,
            },
            title: {
                display: true,
                text: i18n.t("tagsReport.chart.xAxisTitle"),
                font: {
                    size: 12,
                    weight: '600'
                },
                color: '#6B7280',
            }
        }
    }
};

export const TagsReport = () => {
    const classes = useStyles();
    
    // ESTABLECER FECHAS POR DEFECTO - Última semana
    const defaultInitialDate = subDays(new Date(), 7);
    const [initialDate, setInitialDate] = useState(defaultInitialDate);
    const [finalDate, setFinalDate] = useState(new Date());
    const [tagsData, setTagsData] = useState({ labels: [], data: [], colors: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const companyId = localStorage.getItem("companyId");

    const handleGetTagsReport = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await getTagsReport({
                date_from: format(initialDate, 'yyyy-MM-dd'),
                date_to: format(finalDate, 'yyyy-MM-dd'),
                companyId: companyId
            });
            setTagsData(data);
        } catch (error) {
            // ✅ NO MOSTRAR ERROR SI ES 403 (Forbidden) - Probablemente falta token o expiró
            if (error.response && error.response.status === 403) {
                console.warn("⚠️ Error 403 al cargar reporte de etiquetas - Token inválido o expirado");
                // No mostrar toast, el interceptor de API manejará el refresh token
            } else if (error.response && error.response.status !== 404) {
                console.error('Error cargando reporte de etiquetas:', error);
                // Solo mostrar error si no es un error de autenticación
                if (error.response.status !== 401 && error.response.status !== 403) {
                    toast.error(i18n.t("tagsReport.messages.error"));
                }
            } else {
                console.error('Error cargando reporte de etiquetas:', error);
            }
            setTagsData({ labels: [], data: [], colors: [], total: 0 });
        } finally {
            setLoading(false);
        }
    }, [initialDate, finalDate, companyId]);

    // CARGAR DATOS AUTOMÁTICAMENTE AL MOUNT
    useEffect(() => {
        handleGetTagsReport();
    }, [handleGetTagsReport]);

    const dataCharts = {
        labels: tagsData.labels,
        datasets: [
            {
                data: tagsData.data,
                backgroundColor: tagsData.colors,
                borderColor: tagsData.colors.map(color => color + '80'), // Versión transparente para bordes
                borderWidth: 2,
                borderRadius: 4,
                borderSkipped: false,
            }
        ],
    };

    return (
        <Paper className={classes.paper}>
            <Typography component="h2" variant="h6" className={classes.title}>
                <span role="img" aria-label="etiquetas">🏷️</span> {i18n.t("tagsReport.title")}
                {tagsData.total > 0 && (
                    <span style={{ color: '#6B7280', fontWeight: 400, fontSize: '16px' }}>
                        {' '}({tagsData.total})
                    </span>
                )}
            </Typography>
            
            <div className={classes.filtersContainer}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label={i18n.t("tagsReport.filters.start")}
                            type="date"
                            value={format(initialDate, 'yyyy-MM-dd')}
                            onChange={(e) => {
                                const date = new Date(e.target.value);
                                if (!isNaN(date.getTime())) {
                                    setInitialDate(date);
                                }
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label={i18n.t("tagsReport.filters.end")}
                            type="date"
                            value={format(finalDate, 'yyyy-MM-dd')}
                            onChange={(e) => {
                                const date = new Date(e.target.value);
                                if (!isNaN(date.getTime())) {
                                    setFinalDate(date);
                                }
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={handleGetTagsReport}
                            fullWidth
                            style={{ 
                                borderRadius: 8,
                                textTransform: 'none',
                                fontWeight: 600,
                                padding: '8px 16px'
                            }}
                        >
                            {i18n.t("tagsReport.filters.filter")}
                        </Button>
                    </Grid>
                </Grid>
            </div>

            <div className={classes.chartContainer}>
                {loading ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography style={{ color: '#6B7280' }}>{i18n.t("tagsReport.messages.loading")}</Typography>
                    </div>
                ) : tagsData.labels.length > 0 ? (
                    <Bar options={options} data={dataCharts} />
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography style={{ color: '#6B7280' }}>{i18n.t("tagsReport.messages.noData")}</Typography>
                    </div>
                )}
            </div>
        </Paper>
    );
};
